import React from 'react';
import PropTypes from 'prop-types';

import GC from '../../../../util/GlobalConstant';

import FilterDropdown from '../../../../components/FilterDropdown';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import style from './index.scss';
import { ConfigData } from '../../../../util/ABUtil';

/**
 * NoDataPanel
 * @param onTogglePanelVisibility
 * @param onReload
 * @returns {*}
 * @constructor
 */
const NoDataPanel = ({ onTogglePanelVisibility, onReload }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Segments View</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>
              No Alert Status Breakdown data exists for the period selected.
            </p>
            <p>
              <a className={style.retryLink} onClick={onReload}>
                Click here to fetch Alert Status Breakdown data again.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * IsLoadingPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const IsLoadingPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Segments View</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-minimize"></i>
            </h5>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <p>Loading Alert Status Breakdown chart data...</p>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * MinimizedPanel
 * @param onTogglePanelVisibility
 * @returns {*}
 * @constructor
 */
const MinimizedPanel = ({ onTogglePanelVisibility }) => {
  return (
    <div className="card">
      <div className="card-header">
        <div className="row">
          <div className="col-md-6">
            <p>Segments View</p>
          </div>

          <div className="col-md-6">
            <h5 className="text-right">
              <i
                onClick={onTogglePanelVisibility}
                className="fa fa-window-maximize"></i>
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};
/**
 * AlertStatusBreakdownChart
 * @param props
 * @returns {*}
 * @constructor
 */
export const AlertStatusBreakdownChart = (props) => {
  const {
    data,
    isLoading,
    isCollapsed,
    onTogglePanelVisibility,
    onReload,
    onFilter,

    filterGroupData,
    userFilterSetting,
    isAnomaliesAvailable
  } = props;

  if (isCollapsed) {
    return <MinimizedPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }

  if (isLoading) {
    return <IsLoadingPanel onTogglePanelVisibility={onTogglePanelVisibility} />;
  }
  if (Array.isArray(data) && data.length > 0) {
    // let filteredSegments = [];
    // if (userFilterSetting && userFilterSetting.Segments) {
    //   data.map(item => {
    //     if (userFilterSetting.Segments[item.segment]) {
    //       filteredSegments.push(item);
    //     }
    //   });
    // } else {
    //   filteredSegments = data;
    // }
    const sorted = data.sort((x, y) => {
      if (x.segment.toUpperCase() < y.segment.toUpperCase()) return -1;
      if (x.segment.toUpperCase() > y.segment.toUpperCase()) return 1;
      return 0;
    });
    let strBar = null;
    if (userFilterSetting?.Alerts) {
      if (userFilterSetting.Alerts.str) {
        strBar = (
          <Bar name="STR" dataKey="str" stackId="a" fill={GC.strColor} />
        );
      }
    } else {
      strBar = <Bar name="STR" dataKey="str" stackId="a" fill={GC.strColor} />;
    }

    let areAllSegmentsDeselected = true;
    if (userFilterSetting?.Segments) {
      Object.keys(userFilterSetting.Segments).forEach((segment) => {
        if (userFilterSetting.Segments[segment]) {
          areAllSegmentsDeselected = false;
        }
      });
    } else {
      areAllSegmentsDeselected = false;
    }

    let anomalyBar = null;
    if (userFilterSetting?.Alerts) {
      if (userFilterSetting.Alerts.anomaly) {
        anomalyBar = (
          <Bar
            name="Anomaly"
            dataKey="anomaly"
            stackId="a"
            fill={GC.anomalyColor}
          />
        );
      }
    } else {
      anomalyBar = (
        <Bar
          name="Anomaly"
          dataKey="anomaly"
          stackId="a"
          fill={GC.anomalyColor}
        />
      );
    }

    let noIssueBar = null;
    if (userFilterSetting?.Alerts) {
      if (userFilterSetting.Alerts.noIssue) {
        noIssueBar = (
          <Bar
            name="No Issue"
            dataKey="noIssue"
            stackId="a"
            fill={GC.noIssueColor}
          />
        );
      }
    } else {
      noIssueBar = (
        <Bar
          name="No Issue"
          dataKey="noIssue"
          stackId="a"
          fill={GC.noIssueColor}
        />
      );
    }

    return (
      <div className="card">
        <div className="card-header">
          <div className="row">
            <div className="col-md-6">
              <p>Segments View</p>
            </div>

            <div className="col-md-6">
              <h5 className="text-right">
                <i
                  onClick={onTogglePanelVisibility}
                  className="fa fa-window-minimize"></i>
              </h5>
            </div>
          </div>
        </div>

        <div className="card-body">
          <h3>Alert Status Breakdown</h3>
          <div className="row">
            <div className="offset-md-10 col-md-2">
              <FilterDropdown
                filterGroupData={filterGroupData}
                handleConfirm={onFilter}
                userFilterSetting={userFilterSetting}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              {/* Figure Bar Chart goes here */}
              <ResponsiveContainer
                width="100%"
                height={750}
                margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                <BarChart
                  layout="vertical"
                  data={areAllSegmentsDeselected ? [] : sorted}>
                  <XAxis type="number" />
                  <YAxis dataKey="segment" type="category" />

                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip />
                  <Legend />
                  {!ConfigData.IS_NCD_NOT_AVAILABLE && anomalyBar}
                  {noIssueBar}
                  {strBar}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <NoDataPanel
      onReload={onReload}
      onTogglePanelVisibility={onTogglePanelVisibility}
    />
  );
};
/**
 * AlertStatusBreakdownChart proptypes
 * @type {{isLoading: shim, isCollapsed: shim, userFilterSetting: shim, data: shim, onReload: shim, onTogglePanelVisibility: shim, onFilter: shim, filterGroupData: shim}}
 */
AlertStatusBreakdownChart.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onTogglePanelVisibility: PropTypes.func,
  onReload: PropTypes.func,
  onFilter: PropTypes.func,

  filterGroupData: PropTypes.object,
  userFilterSetting: PropTypes.object
};
